import { useState, useEffect, useCallback } from "react";
import { useTheme, DARK, LIGHT } from "../../components/Layout";
import { useNavigate } from "react-router-dom";
import { gregorianToJalaliStr } from "../../utils/PersianDatePicker";
import { presidentAPI } from "../../services/api";

const fmt = (n: any) => new Intl.NumberFormat("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(Number(n) || 0);

function CurrencyLines({ byCurrency, color }: { byCurrency: Record<string, number>; color: string }) {
  const entries = Object.entries(byCurrency || {}).filter(([, v]) => v > 0);
  if (entries.length === 0) return <span style={{ fontSize: 18, fontWeight: 900, color }}>—</span>;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {entries.map(([cur, amt]) => (
        <div key={cur} style={{ display: "flex", alignItems: "baseline", gap: 4 }}>
          <span style={{ fontSize: entries.length > 2 ? 13 : 16, fontWeight: 900, color }}>{fmt(amt)}</span>
          <span style={{ fontSize: 10, fontWeight: 700, color, opacity: 0.75 }}>{cur}</span>
        </div>
      ))}

    </div>
  );
}

const JOURNAL_TYPES = [
  { key: "purchases",    label: "خرید",         color: "#f59e0b", icon: "🛒", route: "/finance/journal/purchase" },
  { key: "sales",        label: "فروش",          color: "#10b981", icon: "💰", route: "/finance/journal/sale" },
  { key: "payments_in",  label: "دریافت پول",    color: "#3b82f6", icon: "📥", route: "/finance/journal/payment-in" },
  { key: "payments_out", label: "پرداخت پول",    color: "#f97316", icon: "📤", route: "/finance/journal/payment-out" },
  { key: "expenses",     label: "مصارف",         color: "#ef4444", icon: "💸", route: "/finance/journal/expense" },
  { key: "cogs",         label: "COGS",           color: "#8b5cf6", icon: "📦", route: "/finance/journal/cogs" },
  { key: "transfers",    label: "انتقال پول",    color: "#06b6d4", icon: "🔄", route: "/finance/journal/transfer" },
];

const ACTION_LABELS: Record<string, string> = {
  login: "ورود به سیستم", logout: "خروج از سیستم",
  add: "ثبت", edit: "ویرایش", delete: "حذف",
  view: "مشاهده", warehouse_tx: "تراکنش گدام", transfer: "انتقال",
};

const PAGE_LABELS: Record<string, string> = {
  auth: "احراز هویت", finance: "مالی", warehouse: "گدام",
  customers: "مشتریان", vendors: "فروشندگان", hr: "منابع انسانی",
};

export default function President() {
  const { dark } = useTheme();
  const T = dark ? DARK : LIGHT;
  const navigate = useNavigate();

  const [tab, setTab] = useState<"journal"|"monitoring">("journal");

  // جورنال
  const [journalStats, setJournalStats] = useState<any>({});
  const [selectedType, setSelectedType] = useState("purchases");
  const [journalRows, setJournalRows]   = useState<any[]>([]);
  const [journalSearch, setJournalSearch] = useState("");
  const today = new Date().toISOString().slice(0,10);
  const monthStart = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0,10);
  const [dateFrom, setDateFrom] = useState(monthStart);
  const [dateTo, setDateTo]     = useState(today);
  const [quickFilter, setQuickFilter] = useState<"today"|"yesterday"|"month"|"all">("month");

  const applyQuickFilter = (q: "today"|"yesterday"|"month"|"all") => {
    setQuickFilter(q);
    const t = new Date();
    const pad = (n: number) => String(n).padStart(2, "0");
    const fmt2 = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`;
    if (q === "today") {
      setDateFrom(fmt2(t)); setDateTo(fmt2(t));
    } else if (q === "yesterday") {
      const y = new Date(t); y.setDate(y.getDate()-1);
      setDateFrom(fmt2(y)); setDateTo(fmt2(y));
    } else if (q === "month") {
      setDateFrom(fmt2(new Date(t.getFullYear(), t.getMonth(), 1))); setDateTo(fmt2(t));
    } else {
      setDateFrom("2020-01-01"); setDateTo(fmt2(t));
    }
  };
  const [loadingStats, setLoadingStats]   = useState(true);
  const [loadingRows, setLoadingRows]     = useState(false);

  // مانیتورینگ
  const [users, setUsers]         = useState<any[]>([]);
  const [logs, setLogs]           = useState<any[]>([]);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [userDetail, setUserDetail]     = useState<any>(null);
  const [logSearch, setLogSearch]       = useState("");
  const [logFrom, setLogFrom]           = useState("2020-01-01");
  const [logTo, setLogTo]               = useState(new Date().toISOString().slice(0,10));
  const [loadingLogs, setLoadingLogs]   = useState(false);
  const [viewTx, setViewTx]             = useState<any>(null); // مشاهده تراکنش

  const loadStats = useCallback(async () => {
    setLoadingStats(true);
    const res = await presidentAPI.journalStats({ from: dateFrom, to: dateTo });
    if (res.success) setJournalStats(res.stats);
    setLoadingStats(false);
  }, [dateFrom, dateTo]);

  const loadRows = useCallback(async () => {
    setLoadingRows(true);
    const res = await presidentAPI.journalList({ type: selectedType, from: dateFrom, to: dateTo, search: journalSearch });
    if (res.success) setJournalRows(res.rows);
    setLoadingRows(false);
  }, [selectedType, dateFrom, dateTo, journalSearch]);

  const loadUsers = useCallback(async () => {
    const res = await presidentAPI.usersSummary();
    if (res.success) setUsers(res.users);
  }, []);

  const loadLogs = useCallback(async () => {
    setLoadingLogs(true);
    const res = await presidentAPI.activityLogs({ from: logFrom, to: logTo, search: logSearch });
    if (res.success) setLogs(res.logs);
    setLoadingLogs(false);
  }, [logFrom, logTo, logSearch]);

  const loadUserDetail = async (uid: number) => {
    const res = await presidentAPI.userDetail(uid);
    if (res.success) {
      setUserDetail(res);
      setSelectedUser(res.user);
      // لود لاگ‌های این کاربر از اول تا آخر
      setLoadingLogs(true);
      const logsRes = await presidentAPI.activityLogs({ user_id: uid, from: "2020-01-01", to: new Date().toISOString().slice(0,10), limit: 500 });
      if (logsRes.success) setLogs(logsRes.logs);
      setLoadingLogs(false);
    }
  };

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { loadRows(); }, [loadRows]);
  useEffect(() => { if (tab === "monitoring") { loadUsers(); loadLogs(); } }, [tab, loadUsers, loadLogs]);

  const inp: React.CSSProperties = {
    padding: "8px 12px", borderRadius: 8, border: `1.5px solid ${dark?"rgba(255,255,255,0.1)":"#e2e8f0"}`,
    background: dark?"rgba(255,255,255,0.05)":"#fff", color: T.text, fontSize: 12,
    fontFamily: "'Vazirmatn','Segoe UI',sans-serif", outline: "none",
  };

  const card: React.CSSProperties = {
    borderRadius: 14, padding: "18px 20px",
    background: dark?"rgba(255,255,255,0.03)":"#fff",
    border: `1px solid ${T.cardBorder}`,
  };

  return (
    <div dir="rtl" style={{ fontFamily: "'Vazirmatn','Segoe UI',sans-serif", padding: "24px 28px 40px" }}>

      {/* هدر */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 11, color: "#8b5cf6", fontWeight: 700, letterSpacing: "1.5px", marginBottom: 4 }}>پنل اجرایی</div>
        <h1 style={{ fontSize: 24, fontWeight: 900, margin: 0, color: T.text }}>صفحه ریاست</h1>
        <p style={{ fontSize: 12, color: T.subtext, margin: "4px 0 0" }}>گزارش جامع معاملات و مانیتورینگ کاربران</p>
      </div>

      {/* تب‌ها */}
      <div style={{ display: "flex", gap: 8, marginBottom: 24 }}>
        {[{ key: "journal", label: "📊 گزارش معاملات" }, { key: "monitoring", label: "👁 مانیتورینگ کاربران" }].map(t => (
          <button key={t.key} onClick={() => setTab(t.key as any)} style={{
            padding: "10px 20px", borderRadius: 10, border: "none", cursor: "pointer",
            background: tab === t.key ? "linear-gradient(135deg,#8b5cf6,#6d28d9)" : dark?"rgba(255,255,255,0.05)":"#f1f5f9",
            color: tab === t.key ? "#fff" : T.subtext, fontSize: 13, fontWeight: 700,
            fontFamily: "'Vazirmatn','Segoe UI',sans-serif",
          }}>{t.label}</button>
        ))}
      </div>

      {/* ==================== تب جورنال ==================== */}
      {tab === "journal" && (
        <div>
          {/* فیلتر سریع زمانی */}
          <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
            <div style={{ display: "flex", gap: 6, background: dark?"rgba(255,255,255,0.04)":"#f1f5f9", borderRadius: 10, padding: 4 }}>
              {([["today","امروز"],["yesterday","دیروز"],["month","این ماه"],["all","همه"]] as const).map(([key, label]) => (
                <button key={key} onClick={() => applyQuickFilter(key)} style={{
                  padding: "7px 14px", borderRadius: 8, border: "none", cursor: "pointer",
                  background: quickFilter === key ? "linear-gradient(135deg,#8b5cf6,#6d28d9)" : "transparent",
                  color: quickFilter === key ? "#fff" : T.subtext,
                  fontSize: 12, fontWeight: 700, fontFamily: "inherit", transition: "all 0.2s",
                }}>{label}</button>
              ))}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ fontSize: 11, color: T.subtext }}>از:</span>
              <input type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setQuickFilter("all"); }} style={{ ...inp, width: 130 }} />
              <span style={{ fontSize: 11, color: T.subtext }}>تا:</span>
              <input type="date" value={dateTo} onChange={e => { setDateTo(e.target.value); setQuickFilter("all"); }} style={{ ...inp, width: 130 }} />
              <button onClick={() => { loadStats(); loadRows(); }} style={{
                padding: "8px 16px", borderRadius: 8, border: "none", cursor: "pointer",
                background: "linear-gradient(135deg,#8b5cf6,#6d28d9)", color: "#fff",
                fontSize: 12, fontWeight: 700, fontFamily: "inherit",
              }}>🔍 اعمال</button>
            </div>
          </div>

          {/* کارت‌های ۷ معامله */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 10, marginBottom: 24 }}>
            {JOURNAL_TYPES.map(jt => {
              const s = journalStats[jt.key] || { count: 0, total: 0, by_currency: {} };
              const isSelected = selectedType === jt.key;
              const entries = Object.entries(s.by_currency || {}).filter(([,v]) => (v as number) > 0);
              return (
                <div key={jt.key} onClick={() => setSelectedType(jt.key)} style={{
                  borderRadius: 14, padding: "14px 12px", cursor: "pointer",
                  border: isSelected ? `2px solid ${jt.color}` : `1px solid ${T.cardBorder}`,
                  background: isSelected ? `${jt.color}14` : dark?"rgba(255,255,255,0.03)":"#fff",
                  transform: isSelected ? "translateY(-3px)" : "none",
                  boxShadow: isSelected ? `0 8px 24px ${jt.color}30` : "none",
                  transition: "all 0.2s",
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
                    <span style={{ fontSize: 18 }}>{jt.icon}</span>
                    <span style={{ fontSize: 11, fontWeight: 700, color: T.subtext }}>{jt.label}</span>
                  </div>
                  <div style={{ fontSize: 22, fontWeight: 900, color: jt.color, marginBottom: 6 }}>
                    {loadingStats ? "..." : fmt(s.count)}
                    <span style={{ fontSize: 10, fontWeight: 600, color: T.subtext, marginRight: 4 }}>معامله</span>
                  </div>
                  {loadingStats ? (
                    <div style={{ fontSize: 11, color: T.subtext }}>...</div>
                  ) : entries.length === 0 ? (
                    <div style={{ fontSize: 13, color: T.subtext }}>—</div>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                      {entries.map(([cur, amt]) => (
                        <div key={cur} style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                          <span style={{ fontSize: entries.length > 2 ? 11 : 13, fontWeight: 800, color: jt.color }}>{fmt(amt as number)}</span>
                          <span style={{ fontSize: 9, fontWeight: 700, color: jt.color, opacity: 0.75, background: `${jt.color}15`, padding: "1px 5px", borderRadius: 4 }}>{cur}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  <button onClick={e => { e.stopPropagation(); navigate(jt.route); }} style={{
                    marginTop: 10, width: "100%", padding: "5px 0", borderRadius: 6, border: "none",
                    background: `${jt.color}20`, color: jt.color, fontSize: 10, fontWeight: 700,
                    cursor: "pointer", fontFamily: "inherit",
                  }}>→ رفتن</button>
                </div>
              );
            })}
          </div>

          {/* جدول معاملات */}
          <div style={card}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, flexWrap: "wrap", gap: 10 }}>
              <div style={{ fontSize: 14, fontWeight: 800, color: T.text }}>
                {JOURNAL_TYPES.find(j => j.key === selectedType)?.icon} لیست {JOURNAL_TYPES.find(j => j.key === selectedType)?.label}
              </div>
              <input value={journalSearch} onChange={e => setJournalSearch(e.target.value)}
                placeholder="جستجو..." style={{ ...inp, minWidth: 200 }} />
            </div>

            {loadingRows ? (
              <div style={{ textAlign: "center", padding: 40, color: T.subtext }}>در حال بارگذاری...</div>
            ) : journalRows.length === 0 ? (
              <div style={{ textAlign: "center", padding: 40, color: T.subtext }}>هیچ رکوردی یافت نشد</div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: dark?"rgba(255,255,255,0.04)":"#f8fafc" }}>
                      {["سریال", "تاریخ", "طرف معامله", "مبلغ", "توضیحات"].map(h => (
                        <th key={h} style={{ padding: "10px 12px", textAlign: "right", fontWeight: 700, color: T.subtext, borderBottom: `1px solid ${T.cardBorder}` }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {journalRows.map((row: any, i: number) => {
                      const jt = JOURNAL_TYPES.find(j => j.key === selectedType);
                      // ارز واقعی و مبلغ اصلی
                      const realCur = row.payment_currency || row.receipt_currency || row.vendor_currency || row.customer_currency || "USD";
                      const realAmt = row.payment_amount || row.receipt_amount || row.net_amount || row.amount || 0;
                      const usdAmt  = row.usd_amount || row.vendor_amount || row.customer_amount || null;
                      return (
                        <tr key={i} style={{ borderBottom: `1px solid ${T.cardBorder}` }}>
                          <td style={{ padding: "9px 12px", fontFamily: "monospace", color: jt?.color, fontSize: 11 }}>{row.serial}</td>
                          <td style={{ padding: "9px 12px", color: T.subtext, fontSize: 11 }}>{row.date ? gregorianToJalaliStr(row.date) : "—"}</td>
                          <td style={{ padding: "9px 12px", fontWeight: 600, color: T.text }}>{row.party_name || "—"}</td>
                          <td style={{ padding: "9px 12px" }}>
                            <div style={{ fontWeight: 800, color: jt?.color, fontSize: 13 }}>{fmt(realAmt)} {realCur}</div>
                            {usdAmt && realCur !== "USD" && <div style={{ fontSize: 10, color: T.subtext }}>≈ ${fmt(usdAmt)}</div>}
                          </td>
                          <td style={{ padding: "9px 12px", color: T.subtext, fontSize: 11 }}>{row.notes || "—"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ==================== تب مانیتورینگ ==================== */}
      {tab === "monitoring" && (
        <div style={{ display: "grid", gridTemplateColumns: "280px 1fr", gap: 20 }}>

          {/* ستون چپ: لیست کاربران */}
          <div>
            <div style={{ ...card, marginBottom: 14 }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: T.text, marginBottom: 12 }}>👥 کاربران سیستم</div>
              {users.length === 0 ? (
                <div style={{ color: T.subtext, fontSize: 12, textAlign: "center", padding: 20 }}>بارگذاری...</div>
              ) : users.map((u: any) => (
                <div key={u.id} onClick={() => loadUserDetail(u.id)} style={{
                  display: "flex", alignItems: "center", gap: 10, padding: "10px 12px",
                  borderRadius: 10, marginBottom: 6, cursor: "pointer",
                  background: selectedUser?.id === u.id ? dark?"rgba(139,92,246,0.15)":"rgba(139,92,246,0.08)" : dark?"rgba(255,255,255,0.03)":"#f8fafc",
                  border: selectedUser?.id === u.id ? "1px solid #8b5cf640" : `1px solid ${T.cardBorder}`,
                }}>
                  <div style={{ width: 36, height: 36, borderRadius: "50%", background: "linear-gradient(135deg,#8b5cf6,#6d28d9)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 13, fontWeight: 700, flexShrink: 0 }}>
                    {u.avatar ? <img src={u.avatar} style={{ width: 36, height: 36, borderRadius: "50%", objectFit: "cover" }} alt="" /> : u.full_name?.charAt(0) || "؟"}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: T.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{u.full_name}</div>
                    <div style={{ fontSize: 10, color: T.subtext }}>{u.total_actions || 0} فعالیت</div>
                  </div>
                  <div style={{ width: 8, height: 8, borderRadius: "50%", background: u.status === "active" ? "#10b981" : "#94a3b8", flexShrink: 0 }} />
                </div>
              ))}
            </div>
          </div>

          {/* ستون راست: لاگ‌ها */}
          <div>
            {/* فیلترها */}
            <div style={{ ...card, marginBottom: 14 }}>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ fontSize: 12, color: T.subtext }}>از:</span>
                  <input type="date" value={logFrom} onChange={e => setLogFrom(e.target.value)} style={inp} />
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ fontSize: 12, color: T.subtext }}>تا:</span>
                  <input type="date" value={logTo} onChange={e => setLogTo(e.target.value)} style={inp} />
                </div>
                <input value={logSearch} onChange={e => setLogSearch(e.target.value)} placeholder="جستجو..." style={{ ...inp, flex: 1, minWidth: 150 }} />
                <button onClick={loadLogs} style={{
                  padding: "8px 16px", borderRadius: 8, border: "none", cursor: "pointer",
                  background: "linear-gradient(135deg,#8b5cf6,#6d28d9)", color: "#fff",
                  fontSize: 12, fontWeight: 700, fontFamily: "'Vazirmatn','Segoe UI',sans-serif",
                }}>🔍 فیلتر</button>
                {selectedUser && (
                  <button onClick={() => { setSelectedUser(null); setUserDetail(null); }} style={{
                    padding: "8px 14px", borderRadius: 8, border: `1px solid ${T.cardBorder}`,
                    background: "transparent", color: T.subtext, fontSize: 12, cursor: "pointer",
                    fontFamily: "'Vazirmatn','Segoe UI',sans-serif",
                  }}>✕ حذف فیلتر کاربر</button>
                )}
              </div>
            </div>

            {/* جزئیات کاربر انتخابی */}
            {userDetail && selectedUser && (
              <div style={{ ...card, marginBottom: 14, borderRight: "3px solid #8b5cf6" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
                  <div style={{ width: 44, height: 44, borderRadius: "50%", background: "linear-gradient(135deg,#8b5cf6,#6d28d9)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 16, fontWeight: 800 }}>
                    {selectedUser.full_name?.charAt(0) || "؟"}
                  </div>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 800, color: T.text }}>{selectedUser.full_name}</div>
                    <div style={{ fontSize: 11, color: T.subtext }}>@{selectedUser.username} — {selectedUser.role}</div>
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {(userDetail.stats || []).slice(0, 6).map((st: any, i: number) => (
                    <span key={i} style={{ fontSize: 10, padding: "3px 8px", borderRadius: 6, background: dark?"rgba(139,92,246,0.15)":"#f5f3ff", color: "#8b5cf6", fontWeight: 600 }}>
                      {PAGE_LABELS[st.page] || st.page}: {st.cnt}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* جدول لاگ‌ها */}
            <div style={card}>
              <div style={{ fontSize: 13, fontWeight: 800, color: T.text, marginBottom: 12 }}>
                📋 {selectedUser ? `فعالیت‌های ${selectedUser.full_name}` : "همه فعالیت‌ها"}
              </div>
              {loadingLogs ? (
                <div style={{ textAlign: "center", padding: 40, color: T.subtext }}>در حال بارگذاری...</div>
              ) : (
                <div style={{ overflowX: "auto", maxHeight: 500, overflowY: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                    <thead style={{ position: "sticky", top: 0 }}>
                      <tr style={{ background: dark?"#1e293b":"#f8fafc" }}>
                        {["زمان", "کاربر", "عملیات", "صفحه", "جزئیات", "IP"].map(h => (
                          <th key={h} style={{ padding: "9px 10px", textAlign: "right", fontWeight: 700, color: T.subtext, borderBottom: `1px solid ${T.cardBorder}`, whiteSpace: "nowrap" }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {(selectedUser && userDetail ? userDetail.logs : logs).map((log: any, i: number) => {
                        const actionColor: Record<string, string> = { login: "#10b981", logout: "#94a3b8", add: "#3b82f6", edit: "#f59e0b", delete: "#ef4444" };
                        const color = actionColor[log.action] || "#64748b";
                        // پیدا کردن مسیر مرتبط برای کلیک
                        const routeMap: Record<string, string> = {
                          finance: "/finance/journal/purchase",
                          warehouse: "/warehouse",
                          customers: "/customers",
                          vendors: "/vendors",
                          hr: "/hr",
                        };
                        const route = routeMap[log.page];
                        return (
                          <tr key={i} style={{ borderBottom: `1px solid ${T.cardBorder}`, cursor: route ? "pointer" : "default" }}
                            onClick={() => route && navigate(route)}>
                            <td style={{ padding: "8px 10px", color: T.subtext, whiteSpace: "nowrap", fontSize: 11 }}>
                              {log.created_at ? (
                                <div>
                                  <div>{log.created_at.slice(0,10)}</div>
                                  <div style={{ fontSize: 10, color: "#8b5cf6", fontFamily: "monospace" }}>{log.created_at.slice(11,19)} 🕐</div>
                                </div>
                              ) : "—"}
                            </td>
                            <td style={{ padding: "8px 10px", fontWeight: 600, color: T.text, whiteSpace: "nowrap" }}>
                              {log.full_name || "—"}
                            </td>
                            <td style={{ padding: "8px 10px" }}>
                              <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 6, background: `${color}18`, color, fontWeight: 700 }}>
                                {ACTION_LABELS[log.action] || log.action}
                              </span>
                            </td>
                            <td style={{ padding: "8px 10px", color: T.subtext, fontSize: 11 }}>
                              {PAGE_LABELS[log.page] || log.page || "—"}
                            </td>
                            <td style={{ padding: "8px 10px", color: T.text, maxWidth: 200 }}>
                              <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{log.details || "—"}</div>
                              {(log.action === "add" || log.action === "edit" || log.action === "delete") && (
                                <button onClick={e => { e.stopPropagation(); setViewTx({ id: log.reference_id, type: log.reference_type, action: log.action, details: log.details, user: log.full_name, time: log.created_at, old_data: log.old_data, new_data: log.new_data }); }} style={{ fontSize: 10, padding: "2px 8px", borderRadius: 5, border: "1px solid #3b82f6", background: "transparent", color: "#3b82f6", cursor: "pointer", marginTop: 3, fontFamily: "inherit" }}>
                                  👁 مشاهده
                                </button>
                              )}
                            </td>
                            <td style={{ padding: "8px 10px", color: T.subtext, fontSize: 10, fontFamily: "monospace" }}>
                              {log.ip || "—"}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {(selectedUser && userDetail ? userDetail.logs : logs).length === 0 && (
                    <div style={{ textAlign: "center", padding: 40, color: T.subtext }}>هیچ فعالیتی یافت نشد</div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {/* مودال مشاهده تراکنش */}
      {viewTx && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }} onClick={() => setViewTx(null)}>
          <div onClick={e => e.stopPropagation()} dir="rtl" style={{
            background: dark?"#1e293b":"#fff", borderRadius: 18, padding: 0,
            width: "min(900px, 95vw)", maxHeight: "90vh", overflow: "hidden",
            border: `1px solid ${dark?"rgba(255,255,255,0.12)":"#e2e8f0"}`,
            boxShadow: "0 24px 80px rgba(0,0,0,0.5)", fontFamily: "inherit",
            display: "flex", flexDirection: "column",
          }}>
            {/* هدر */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "20px 24px", borderBottom: `1px solid ${dark?"rgba(255,255,255,0.08)":"#f1f5f9"}`, background: dark?"rgba(139,92,246,0.1)":"#faf5ff", flexShrink: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ width: 40, height: 40, borderRadius: 12, background: viewTx.action==="add"?"linear-gradient(135deg,#10b981,#059669)":viewTx.action==="edit"?"linear-gradient(135deg,#f59e0b,#d97706)":"linear-gradient(135deg,#ef4444,#dc2626)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>
                  {viewTx.action==="add"?"➕":viewTx.action==="edit"?"✏️":"🗑️"}
                </div>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 800, color: dark?"#f0eeff":"#1e1b2e" }}>جزئیات تراکنش</div>
                  <div style={{ fontSize: 11, color: dark?"#94a3b8":"#64748b" }}>{ACTION_LABELS[viewTx.action] || viewTx.action} — {viewTx.type || "—"}</div>
                </div>
              </div>
              <button onClick={() => setViewTx(null)} style={{ background: dark?"rgba(255,255,255,0.1)":"#f1f5f9", border: "none", cursor: "pointer", color: dark?"#94a3b8":"#64748b", fontSize: 18, width: 36, height: 36, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center" }}>✕</button>
            </div>

            {/* محتوا — scrollable */}
            <div style={{ padding: "20px 24px", overflowY: "auto", flex: 1 }}>

              {/* اطلاعات پایه */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 10, marginBottom: 16 }}>
                {[
                  { label: "کاربر", value: viewTx.user, color: "#8b5cf6" },
                  { label: "عملیات", value: ACTION_LABELS[viewTx.action] || viewTx.action, color: viewTx.action==="add"?"#10b981":viewTx.action==="edit"?"#f59e0b":"#ef4444" },
                  { label: "نوع تراکنش", value: viewTx.type || "—", color: "#3b82f6" },
                  { label: "زمان", value: viewTx.time?.slice(0,19) || "—", color: dark?"#94a3b8":"#64748b" },
                ].map((item, i) => (
                  <div key={i} style={{ padding: "12px 14px", borderRadius: 10, background: dark?"rgba(255,255,255,0.04)":"#f8fafc", border: `1px solid ${dark?"rgba(255,255,255,0.06)":"#e2e8f0"}` }}>
                    <div style={{ fontSize: 10, color: dark?"#94a3b8":"#64748b", marginBottom: 4, fontWeight: 600 }}>{item.label}</div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: item.color, direction: item.label==="زمان"?"ltr":"rtl" }}>{item.value}</div>
                  </div>
                ))}
              </div>

              {/* جزئیات */}
              <div style={{ padding: "12px 16px", borderRadius: 10, background: dark?"rgba(139,92,246,0.08)":"#faf5ff", border: "1px solid rgba(139,92,246,0.2)", marginBottom: 16 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#8b5cf6", marginBottom: 6 }}>📝 جزئیات</div>
                <div style={{ fontSize: 13, color: dark?"#f0eeff":"#1e1b2e", lineHeight: 1.8 }}>{viewTx.details || "—"}</div>
              </div>

              {/* قبل و بعد از ویرایش */}
              {viewTx.old_data && viewTx.new_data ? (
                <div>
                  <div style={{ fontSize: 13, fontWeight: 800, color: dark?"#f0eeff":"#1e1b2e", marginBottom: 12 }}>🔄 مقایسه قبل و بعد از ویرایش</div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                    {/* قبل */}
                    <div style={{ borderRadius: 12, border: "1.5px solid rgba(239,68,68,0.3)", overflow: "hidden" }}>
                      <div style={{ padding: "10px 14px", background: dark?"rgba(239,68,68,0.15)":"#fef2f2", fontSize: 12, fontWeight: 800, color: "#ef4444" }}>📋 قبل از ویرایش</div>
                      <div style={{ padding: "12px 14px" }}>
                        {Object.entries(JSON.parse(viewTx.old_data || "{}"))
                          .filter(([k]) => !['created_by','id'].includes(k))
                          .map(([k, v]) => (
                          <div key={k} style={{ display: "flex", gap: 8, padding: "5px 0", borderBottom: `1px solid ${dark?"rgba(255,255,255,0.05)":"#f1f5f9"}` }}>
                            <span style={{ fontSize: 11, fontWeight: 700, color: "#ef4444", minWidth: 120, flexShrink: 0 }}>{k}</span>
                            <span style={{ fontSize: 11, color: dark?"#fca5a5":"#7f1d1d", wordBreak: "break-all" as const }}>{String(v ?? "—")}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    {/* بعد */}
                    <div style={{ borderRadius: 12, border: "1.5px solid rgba(16,185,129,0.3)", overflow: "hidden" }}>
                      <div style={{ padding: "10px 14px", background: dark?"rgba(16,185,129,0.15)":"#f0fdf4", fontSize: 12, fontWeight: 800, color: "#10b981" }}>✅ بعد از ویرایش</div>
                      <div style={{ padding: "12px 14px" }}>
                        {Object.entries(JSON.parse(viewTx.new_data || "{}"))
                          .filter(([k]) => !['created_by','id'].includes(k))
                          .map(([k, v]) => {
                          const oldParsed = JSON.parse(viewTx.old_data || "{}");
                          const changed = String(oldParsed[k] ?? "") !== String(v ?? "");
                          return (
                            <div key={k} style={{ display: "flex", gap: 8, padding: "5px 0", borderBottom: `1px solid ${dark?"rgba(255,255,255,0.05)":"#f1f5f9"}`, background: changed?"rgba(16,185,129,0.08)":"transparent", borderRadius: 4, paddingRight: 4 }}>
                              <span style={{ fontSize: 11, fontWeight: changed?800:700, color: changed?"#10b981":(dark?"#6ee7b7":"#065f46"), minWidth: 120, flexShrink: 0 }}>{k}{changed?" ✱":""}</span>
                              <span style={{ fontSize: 11, color: dark?"#6ee7b7":"#065f46", wordBreak: "break-all" as const, fontWeight: changed?700:400 }}>{String(v ?? "—")}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              ) : viewTx.new_data ? (
                // ثبت جدید — فقط new_data داره
                <div>
                  <div style={{ fontSize: 13, fontWeight: 800, color: dark?"#f0eeff":"#1e1b2e", marginBottom: 12 }}>📋 جزئیات کامل تراکنش</div>
                  <div style={{ borderRadius: 12, border: "1.5px solid rgba(16,185,129,0.3)", overflow: "hidden" }}>
                    <div style={{ padding: "10px 14px", background: dark?"rgba(16,185,129,0.15)":"#f0fdf4", fontSize: 12, fontWeight: 800, color: "#10b981" }}>✅ اطلاعات ثبت شده</div>
                    <div style={{ padding: "12px 14px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                      {Object.entries(JSON.parse(viewTx.new_data || "{}"))
                        .filter(([k]) => !['created_by'].includes(k))
                        .map(([k, v]) => (
                        <div key={k} style={{ display: "flex", gap: 8, padding: "5px 0", borderBottom: `1px solid ${dark?"rgba(255,255,255,0.05)":"#f1f5f9"}` }}>
                          <span style={{ fontSize: 11, fontWeight: 700, color: "#10b981", minWidth: 110, flexShrink: 0 }}>{k}</span>
                          <span style={{ fontSize: 11, color: dark?"#d1fae5":"#065f46", wordBreak: "break-all" as const }}>{String(v ?? "—")}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div style={{ padding: "12px 16px", borderRadius: 10, background: dark?"rgba(100,116,139,0.08)":"#f8fafc", border: `1px solid ${dark?"rgba(255,255,255,0.08)":"#e2e8f0"}`, fontSize: 12, color: T.subtext }}>
                  ℹ️ اطلاعات قبل/بعد ویرایش برای این لاگ ذخیره نشده — این تراکنش قبل از فعال‌سازی سیستم ثبت شده است.
                </div>
              )}
            </div>

            {/* فوتر */}
            <div style={{ padding: "14px 24px", borderTop: `1px solid ${dark?"rgba(255,255,255,0.08)":"#f1f5f9"}`, background: dark?"rgba(251,191,36,0.06)":"#fffbeb", display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
              <span style={{ fontSize: 11, color: dark?"#fbbf24":"#92400e" }}>⚠️ فقط قابل مشاهده — ویرایش یا حذف از اینجا امکان‌پذیر نیست</span>
              <button onClick={() => setViewTx(null)} style={{ padding: "8px 20px", borderRadius: 9, border: "none", background: dark?"rgba(255,255,255,0.1)":"#e2e8f0", color: dark?"#f0eeff":"#1e293b", cursor: "pointer", fontSize: 13, fontWeight: 700, fontFamily: "inherit" }}>بستن</button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}